var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
};
import * as React from 'react';
import { Component } from 'react';
import { ThemeConfig } from 'docz';
import styled from 'react-emotion';
import rgba from 'polished/lib/color/rgba';
import BaseCheck from 'react-feather/dist/icons/check';
import Clipboard from 'react-feather/dist/icons/clipboard';
import { Controlled as CodeMirror } from 'react-codemirror2';
import PerfectScrollbar from 'react-perfect-scrollbar';
import copy from 'copy-text-to-clipboard';
import getter from 'lodash/get';
import { ButtonSwap } from '../ButtonSwap';
import { ButtonLink } from '../Button';
import * as themes from '@styles/codemirror';
import { get } from '@utils/theme';
import './ps-scrollbar';
import 'codemirror/mode/markdown/markdown';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/mode/jsx/jsx';
import 'codemirror/mode/css/css';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/addon/edit/closetag';
import 'codemirror/addon/fold/xml-fold';
const getLanguage = (children) => {
    const defaultLanguage = 'jsx';
    if (typeof children === 'string')
        return defaultLanguage;
    const language = getter(children, 'props.props.className') || defaultLanguage;
    const result = language.replace('language-', '');
    if (result === 'js' || result === 'javascript')
        return 'jsx';
    if (result === 'ts' || result === 'tsx' || result === 'typescript') {
        return 'text/typescript';
    }
    return result;
};
const getChildren = (children) => children && typeof children !== 'string'
    ? getter(children, 'props.children')
    : children;
const Wrapper = styled('div') `
  margin: 30px 0;
  position: relative;
  width: 100%;
  border: 1px solid ${get('colors.border')};
  border-radius: ${get('radii')};
`;
const Scrollbar = styled(PerfectScrollbar) `
  overflow: auto;
  position: relative;
  max-height: 360px;
  border-radius: ${get('radii')};

  .ps__rail-y {
    z-index: 9;
    opacity: 0.4;
  }
`;
const preStyles = get('styles.pre');
const EditorStyled = styled(CodeMirror) `
  ${themes.dark()};
  ${themes.light()};
  ${p => p.theme.docz.mq(preStyles(p))};
  position: relative;
  border-radius: ${(p) => p.square ? 'none' : get('radii')};
  flex: 1;

  .CodeMirror {
    max-width: 100%;
    height: 100%;
  }

  .CodeMirror pre {
    ${p => p.theme.docz.mq(preStyles(p))};
  }

  .CodeMirror-gutters {
    left: 1px !important;
  }

  .CodeMirror-lines {
    padding: 10px 0;
  }

  .CodeMirror-line {
    padding: 0 10px;
  }

  .CodeMirror-linenumber {
    padding: 0 7px 0 5px;
  }
`;
const Actions = styled('div') `
  z-index: 999;
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5px 10px;
  background: transparent;
`;
const textColor = get('colors.text');
export const ActionButton = styled(ButtonSwap) `
  padding: 4px;
  background: transparent;
  font-size: 12px;
  text-transform: uppercase;
  color: ${p => rgba(textColor(p), 0.4)};
  transition: color 0.3s;

  &:hover {
    color: ${p => rgba(textColor(p), 0.7)};
  }
`;
const Check = styled(BaseCheck) `
  stroke: ${get('colors.primary')};
`;
export const ClipboardAction = (_a) => {
    var { content } = _a, props = __rest(_a, ["content"]);
    return (React.createElement(ActionButton, Object.assign({}, props, { as: ButtonLink, title: "Copy to clipboard", onClick: () => copy(content), swap: React.createElement(Check, { width: 17 }) }),
        React.createElement(Clipboard, { width: 15 })));
};
export class Editor extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            code: getChildren(this.props.children),
        };
        this.removeLastLine = (editor) => {
            if (editor && !this.props.withLastLine && this.props.readOnly) {
                const lastLine = editor.lastLine();
                editor.doc.replaceRange('', { line: lastLine - 1 }, { line: lastLine });
            }
        };
        this.handleChange = (editor, data, code) => {
            this.props.onChange && this.props.onChange(code);
            this.setState({ code });
        };
    }
    render() {
        const { code } = this.state;
        const _a = this.props, { mode, children, actions, onChange, className, editorClassName, language: defaultLanguage, square } = _a, props = __rest(_a, ["mode", "children", "actions", "onChange", "className", "editorClassName", "language", "square"]);
        const language = defaultLanguage || getLanguage(children);
        const options = Object.assign({}, props, { tabSize: 2, mode: language || mode, lineNumbers: true, lineWrapping: true, autoCloseTags: true, theme: 'docz-light' });
        const editorProps = (config) => ({
            value: this.state.code,
            className: editorClassName,
            editorDidMount: this.removeLastLine,
            onBeforeChange: this.handleChange,
            options: Object.assign({}, options, { theme: config && config.themeConfig
                    ? config.themeConfig.codemirrorTheme
                    : options.theme }),
        });
        const scrollbarOpts = {
            wheelSpeed: 2,
            wheelPropagation: true,
            minScrollbarLength: 20,
            suppressScrollX: true,
        };
        return (React.createElement(Wrapper, { className: className },
            React.createElement(ThemeConfig, null, config => (React.createElement(Scrollbar, { option: scrollbarOpts },
                React.createElement(EditorStyled, Object.assign({}, editorProps(config), { square: square }))))),
            React.createElement(Actions, null, actions || React.createElement(ClipboardAction, { content: code }))));
    }
}
Editor.defaultProps = {
    mode: 'js',
    readOnly: true,
    matchBrackets: true,
    indentUnit: 2,
};
//# sourceMappingURL=data:application/json;base64,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